import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { clsx } from 'clsx';
import { Mail, Lock, Eye, EyeOff, User, CreditCard } from 'lucide-react';
import { useAuthStore, useThemeStore } from '../store';
import { login, register, getAuthProviders, getCurrentUser } from '../utils/api';
import { redirectToCheckout } from '../utils/stripe';

// State passed when redirecting from pricing
interface LocationState {
  returnTo?: 'subscribe';
  tier?: string;
  priceId?: string;
  message?: string;
}

type Mode = 'login' | 'register';

interface AuthProviders {
  email_password: boolean;
  google: boolean;
  twitter: boolean;
}

// Detect if user is in an in-app browser (Instagram, Facebook, Twitter, etc.)
function isInAppBrowser(): boolean {
  const ua = navigator.userAgent || navigator.vendor || '';
  // Common in-app browser indicators
  return /FBAN|FBAV|Instagram|Twitter|Line|Snapchat|LinkedIn/i.test(ua) ||
    // iOS webview
    (/iPhone|iPod|iPad/i.test(ua) && !/Safari/i.test(ua)) ||
    // Android webview
    /wv\)/.test(ua);
}

export function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login: setAuth, isAuthenticated, user } = useAuthStore();
  const { setTheme } = useThemeStore();
  const inAppBrowser = isInAppBrowser();

  // Get state passed from pricing page
  const locationState = location.state as LocationState | null;
  const subscribeMessage = locationState?.message;
  const subscribePriceId = locationState?.priceId;

  const [mode, setMode] = useState<Mode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [providers, setProviders] = useState<AuthProviders | null>(null);
  const [loadingProviders, setLoadingProviders] = useState(true);

  // Check if on app subdomain or localhost (for development)
  // In production: true for app.stocksentiment.io
  // In development: true for localhost (to test app functionality)
  const hostname = window.location.hostname;
  const isAppSubdomain = hostname.startsWith('app.') || hostname === 'localhost';
  const dashboardPath = isAppSubdomain ? '/' : '/app';
  const settingsPath = isAppSubdomain ? '/settings#subscription' : '/app/settings#subscription';

  // Get API base URL
  const getApiBaseUrl = () => {
    return import.meta.env.VITE_API_URL || '';
  };

  // State for checkout redirect
  const [redirectingToCheckout, setRedirectingToCheckout] = useState(false);

  // Handle post-login redirect (including checkout flow)
  const handlePostLoginRedirect = async (userEmail?: string) => {
    // If user was trying to subscribe, proceed with checkout
    if (subscribePriceId) {
      setRedirectingToCheckout(true);
      try {
        await redirectToCheckout(subscribePriceId, userEmail);
        return; // redirectToCheckout will redirect to Stripe
      } catch (err) {
        console.error('Checkout redirect failed:', err);
        setRedirectingToCheckout(false);
        // Fall back to settings page
        navigate(settingsPath);
        return;
      }
    }
    // Default: go to dashboard
    navigate(dashboardPath);
  };

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      handlePostLoginRedirect(user.email);
    }
  }, [isAuthenticated, user]);

  // Handle OAuth callback - parse token from URL hash
  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.includes('access_token')) {
      const params = new URLSearchParams(hash.substring(1));
      const accessToken = params.get('access_token');

      if (accessToken) {
        // Clean up URL immediately
        window.history.replaceState(null, '', window.location.pathname);

        // Fetch user info and complete login
        const completeOAuthLogin = async () => {
          try {
            const fetchedUser = await getCurrentUser(accessToken);
            setAuth(fetchedUser, accessToken);
            // Handle post-login redirect (including checkout if needed)
            await handlePostLoginRedirect(fetchedUser.email);
          } catch (err) {
            console.error('OAuth login error:', err);
            setError('OAuth login failed. Please try again.');
          }
        };
        completeOAuthLogin();
      }
    }
  }, [setAuth, navigate]);

  // Fetch available auth providers
  useEffect(() => {
    setLoadingProviders(true);
    getAuthProviders()
      .then(setProviders)
      .catch((err) => {
        console.error('Failed to fetch auth providers:', err);
        // Default to OAuth if we can't fetch providers (safer than showing email form)
        setProviders({ email_password: false, google: true, twitter: true });
      })
      .finally(() => setLoadingProviders(false));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      let userEmail: string | undefined;
      if (mode === 'login') {
        const response = await login(email, password);
        setAuth(response.user, response.access_token);
        userEmail = response.user.email;
      } else {
        const response = await register(email, password, name);
        setAuth(response.user, response.access_token);
        setTheme('system'); // Default to system theme for new accounts
        userEmail = response.user.email;
      }
      await handlePostLoginRedirect(userEmail);
    } catch (err: unknown) {
      console.error('Auth error:', err);
      // Extract error message from axios error response
      let message = 'Authentication failed';
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { detail?: string } } };
        message = axiosError.response?.data?.detail || message;
      } else if (err instanceof Error) {
        message = err.message;
      }
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleAuth = () => {
    // Navigate directly to OAuth endpoint - redirect back to login to handle token
    const apiBase = getApiBaseUrl();
    window.location.href = `${apiBase}/api/auth/google?redirect_url=` + encodeURIComponent(window.location.origin + '/login');
  };

  const handleTwitterAuth = () => {
    // Navigate directly to OAuth endpoint - redirect back to login to handle token
    const apiBase = getApiBaseUrl();
    window.location.href = `${apiBase}/api/auth/twitter?redirect_url=` + encodeURIComponent(window.location.origin + '/login');
  };

  // Show full-screen loading when redirecting to Stripe checkout
  if (redirectingToCheckout) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#060606] px-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-white mb-2">Redirecting to payment...</h2>
          <p className="text-gray-400">You'll be redirected to Stripe to complete your purchase.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#060606] px-4 relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px',
          }}
        />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-emerald-500/5 rounded-full blur-[120px]" />
      </div>

      <div className="w-full max-w-md relative z-10">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3">
            <img src="/logo.svg" alt="SentimentEdge" className="h-12 w-12" />
            <span className="text-2xl font-bold">
              <span className="text-white">Sentiment</span>
              <span className="text-emerald-400">Edge</span>
            </span>
          </Link>
          <p className="text-gray-400 mt-3 text-lg">
            {mode === 'login' ? 'Welcome back' : 'Create your account'}
          </p>
        </div>

        {/* In-app browser warning */}
        {inAppBrowser && (
          <div className="mb-6 p-4 rounded-xl bg-amber-500/10 border border-amber-500/30">
            <p className="text-sm text-amber-300 font-medium mb-2">
              Open in your browser for best experience
            </p>
            <p className="text-xs text-amber-300/70 mb-3">
              Google Sign-In doesn't work in in-app browsers. Tap the menu (•••) and select "Open in Safari" or "Open in Browser".
            </p>
            <button
              onClick={() => {
                navigator.clipboard.writeText(window.location.href);
                alert('Link copied! Paste it in Safari or Chrome.');
              }}
              className="w-full py-2 px-3 text-xs font-medium rounded-lg bg-amber-500/20 text-amber-300 hover:bg-amber-500/30 transition-colors"
            >
              Copy Link to Clipboard
            </button>
          </div>
        )}

        {/* Subscription message - shown when redirected from pricing */}
        {subscribeMessage && (
          <div className="mb-6 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30">
            <div className="flex items-center gap-3">
              <CreditCard className="h-5 w-5 text-emerald-400 flex-shrink-0" />
              <p className="text-sm text-emerald-300">
                {subscribeMessage}
              </p>
            </div>
          </div>
        )}

        {/* Auth Card */}
        <div className="bg-[#0c0c0c] rounded-2xl border border-white/10 p-8 shadow-2xl shadow-black/50">
          {/* Loading state */}
          {loadingProviders && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
            </div>
          )}

          {/* Social Auth - only show if providers are available */}
          {!loadingProviders && providers && (providers.google || providers.twitter) && (
            <>
              <div className="space-y-3 mb-6">
                {providers.google && (
                  <button
                    onClick={handleGoogleAuth}
                    className={clsx(
                      'w-full flex items-center justify-center gap-3 h-12 rounded-xl',
                      'bg-white text-gray-900 font-medium cursor-pointer',
                      'hover:bg-gray-100 transition-all duration-300',
                      'hover:shadow-lg hover:shadow-white/10'
                    )}
                  >
                    <svg className="h-5 w-5" viewBox="0 0 24 24">
                      <path
                        fill="#4285F4"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="#34A853"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="#FBBC05"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="#EA4335"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                    Continue with Google
                  </button>
                )}

                {providers.twitter && (
                  <button
                    onClick={handleTwitterAuth}
                    className={clsx(
                      'w-full flex items-center justify-center gap-3 h-12 rounded-xl',
                      'bg-white/5 text-white font-medium border border-white/10 cursor-pointer',
                      'hover:bg-white/10 transition-all duration-300'
                    )}
                  >
                    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                    </svg>
                    Continue with X
                  </button>
                )}
              </div>

              {/* Divider - only show if email/password is also enabled */}
              {providers?.email_password && (
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-white/10" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-[#0c0c0c] text-gray-500">
                      or continue with email
                    </span>
                  </div>
                </div>
              )}
            </>
          )}

          {!loadingProviders && error && (
            <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Email/Password form - only show if enabled */}
          {!loadingProviders && providers?.email_password && (
            <>
              <form onSubmit={handleSubmit} className="space-y-4">
                {mode === 'register' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Name
                    </label>
                    <div className="relative">
                      <User className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Your name"
                        className={clsx(
                          'w-full h-12 pl-12 pr-4 rounded-xl',
                          'bg-white/5 border border-white/10',
                          'text-white placeholder:text-gray-500',
                          'focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500/50',
                          'transition-all duration-300'
                        )}
                      />
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Email
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      required
                      className={clsx(
                        'w-full h-12 pl-12 pr-4 rounded-xl',
                        'bg-white/5 border border-white/10',
                        'text-white placeholder:text-gray-500',
                        'focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500/50',
                        'transition-all duration-300'
                      )}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      minLength={8}
                      className={clsx(
                        'w-full h-12 pl-12 pr-12 rounded-xl',
                        'bg-white/5 border border-white/10',
                        'text-white placeholder:text-gray-500',
                        'focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500/50',
                        'transition-all duration-300'
                      )}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors cursor-pointer"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className={clsx(
                    'w-full h-12 rounded-xl font-semibold transition-all duration-300',
                    'bg-gradient-to-r from-emerald-500 to-teal-500 text-black',
                    'hover:shadow-lg hover:shadow-emerald-500/25',
                    'disabled:opacity-50 disabled:cursor-not-allowed'
                  )}
                >
                  {loading ? 'Please wait...' : mode === 'login' ? 'Sign In' : 'Create Account'}
                </button>
              </form>

              {/* Switch Mode */}
              <p className="text-center text-sm text-gray-400 mt-6">
                {mode === 'login' ? (
                  <>
                    Don't have an account?{' '}
                    <button
                      onClick={() => setMode('register')}
                      className="text-emerald-400 font-medium hover:text-emerald-300 transition-colors cursor-pointer"
                    >
                      Sign up
                    </button>
                  </>
                ) : (
                  <>
                    Already have an account?{' '}
                    <button
                      onClick={() => setMode('login')}
                      className="text-emerald-400 font-medium hover:text-emerald-300 transition-colors cursor-pointer"
                    >
                      Sign in
                    </button>
                  </>
                )}
              </p>
            </>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-gray-600 mt-6">
          By continuing, you agree to our{' '}
          <Link to="/terms" className="text-emerald-400/70 hover:text-emerald-400 transition-colors">
            Terms of Service
          </Link>{' '}
          and{' '}
          <Link to="/privacy" className="text-emerald-400/70 hover:text-emerald-400 transition-colors">
            Privacy Policy
          </Link>
        </p>
      </div>
    </div>
  );
}
